import { useId } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Select } from '../../ui/Select';
import { nav } from '../../copy/shell';
import { containers as containersCopy } from '../../copy/containers';
import { containerPath, sectionOf, type ContainerSection } from '../containers/paths';
import { useCurrentContainer } from '../containers/useCurrentContainer';
import { NavItem } from './NavItem';
import s from './SideNav.module.css';

/**
 * The nav's two groups, for the sidebar and the drawer alike: the container switcher over
 * the current container's pages, then the tenant's under their own label. The label is
 * visible because it is what tells a reader the first group changes with the switcher
 * and the second does not.
 *
 * Until the containers are read, the container pages link to the flat paths, which
 * redirect into the current container once they are; the nav never waits on the list.
 * The switcher's place is held meanwhile, so the items under it do not move when it
 * arrives. Switching goes to the same page of the new container, or its Overview from a
 * key's page — the key stays in its own — and from a tenant page.
 */
export function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const selectId = useId();
  const tenantLabelId = useId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { current, containers } = useCurrentContainer();
  const list = containers.data ?? [];
  const onKeyPage = /^\/containers\/[^/]+\/keys\/[^/]+/.test(pathname);
  const stay = (section: ContainerSection) => (current ? containerPath(current.id, section) : `/${section}`);
  const forward = onNavigate ? { onNavigate } : {};

  return (
    <>
      <div className={s.group}>
        {list.length > 0 && current ? (
          <div className={s.switcher}>
            <label className={s.groupLabel} htmlFor={selectId}>
              {containersCopy.switcher.label}
            </label>
            <Select
              id={selectId}
              value={current.id}
              onChange={(e) => {
                const section = onKeyPage ? 'overview' : (sectionOf(pathname) ?? 'overview');
                void navigate(containerPath(e.target.value, section));
                onNavigate?.();
              }}
            >
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        ) : containers.data === undefined ? (
          // The switcher's place until the list is read, a first read that failed included:
          // its label over a disabled select of the same height, so nothing under it moves
          // when the real one arrives. It is a picture of the switcher, not a control, so
          // assistive technology is not shown it. Empty, because the sidebar's select is
          // too narrow for "Finding the container" and would clip it under the chevron.
          <div key="placeholder" className={s.switcher} aria-hidden="true">
            <span className={s.groupLabel}>{containersCopy.switcher.label}</span>
            <Select disabled />
          </div>
        ) : null}
        <div className={s.items}>
          {nav.containerItems.map((item) => (
            <NavItem key={item.section} to={stay(item.section)} label={item.label} {...forward} />
          ))}
        </div>
      </div>

      <div className={s.group}>
        <span id={tenantLabelId} className={s.groupLabel}>
          {nav.tenantGroup}
        </span>
        <div className={s.items} role="group" aria-labelledby={tenantLabelId}>
          {nav.tenantItems.map((item) => (
            <NavItem key={item.to} to={item.to} label={item.label} end={item.to === '/containers'} {...forward} />
          ))}
        </div>
      </div>
    </>
  );
}

export default NavItems;
